// 261023 E3-P3i — nút đỏ «Bắt đầu» GỬI LUÔN (không mở hộp; User 26/10 «Bỏ hộp, bấm là chạy luôn»): giá trị = đầu vào đã lưu hoặc mặc định của hộp, số tập trống; hộp cài đặt (D6) còn ở menu «⋯» của dải: «Bắt đầu với cài đặt khác…». Quyết định ở `newStoryQuickStart.ts`.
// 261023 E3-P3c1 — dải "Truyện mới": nút Bắt đầu (+ khung nhập D6), dải ba chặng, nút chính theo chỗ dừng. Quyết định ở `newStoryRunView.ts`; file này chỉ vẽ + gọi API.
// 261023 E3-P3h5a — KHÔNG hiện số tiền: nút «Bắt đầu» / «Duyệt & chạy tiếp» vẫn tự gửi `expectedMaxUsd`; dải «Chờ bạn quyết» có nút «Chạy tiếp» (cầu nối B11: gọi route nâng-và-chạy-tiếp ĐÚNG MỘT lần, không có khung nhập số). Quyết định ở `newStoryBudgetView.ts`.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Dropdown, Input, InputNumber, Modal, Select, Space, Tooltip, Typography, message } from 'antd';
import Button from './WbButton';
import { adaptationApi } from '../../../services/api/adaptation';
import type { NewStoryEstimate } from '../../../services/api/adaptation';
import type { WorkbenchStageKey } from '../../../types/adaptationWorkbench';
import {
  ADAPT_MODE_LABEL, defaultStartForm, newStoryBar, startEpisodesHint, NsAction, nsApproveBlock, nsCancelCopy, nsProgressText, POLICY_MODE_LABEL, startBodyOf, startFormErrors,
  StartForm, waitBudgetCtxOf, planStopReasonLines,
} from './newStoryRunView';
import { preflightOf } from './preflightView';
import { bandItemsOf, compactBandOf } from './compactBandView';
import type { BandItem } from './compactBandView';
import { autoFixTotals } from './episodeRowView';
import { quickStart, savedStartForm } from './newStoryQuickStart';
import { redactMoney } from './moneyRedact';
import {
  autoContinueErrorView, autoContinueOutcomeView, budgetOf, continueErrorView, continueOutcomeView, estimateView, sessionPendingStore, startErrorView, stop1ErrorView, submitContinue, waitBudgetView,
} from './newStoryBudgetView';
import { askConfirm, handleAppendError } from './appendRunActions';
import { gateWaitView } from './chainGateView';
import { VOLUME_GATE_NOTHING_TO_RERUN, volumeGateWaitView } from './volumeGateView';
import { errorShouldReload, overviewErrorView } from './overview/overviewErrors';
import { episodePlanSaveErrorView, submitEpisodePlanApproval } from './episodePlanView';
import { useCtaHolds, useWorkbenchCtx } from './WorkbenchContext';
import BandCtaReporter from './BandCtaReporter';
import HandoffBatchResults from './HandoffBatchResults';
import ChainLoop from './ChainLoop';
import { bandChainOf } from './chainProgressView';
import { batchItemsOf, isPushable, pushStripOf } from './handoffBatchView';
import { useHandoffBatchActions, useHandoffPreview } from './useHandoffBatch';
import { recheckStripOf } from './recheckView';
import { rejudgeFailureToast } from './rejudgeView';
import { autoRepairStripOf } from './autoRepairView';
import { useAutoRepair } from './useAutoRepair';

const { Text } = Typography;

interface Props { stage: WorkbenchStageKey; dismissedId: string | null; onDismiss: (id: string) => void }

const BAND_TIP = 'Chặng 1: máy quét nguồn + dựng khung xương, rồi dừng chờ bạn duyệt. Chặng 2: máy bản địa hoá, chia tập, viết mọi tập, rồi dừng chờ bạn xem lướt. Chặng 3: đẩy sang Chapters. Mỗi bước máy chấm và tự sửa tối đa 2 vòng; còn lỗi thì máy vẫn chạy tiếp và ghi vào Sổ duyệt.';

const NewStoryRunBar: React.FC<Props> = ({ stage, dismissedId, onDismiss }) => {
  const { storyId, workbench, data, dataVersion, refreshAll, goTo, busy, openReview, reviewNeed, reviewing } = useWorkbenchCtx();
  const [working, setWorking] = useState(false);
  const autoRepair = useAutoRepair();   // 3j2b-2: «Để máy sửa N tập» / «Dừng»
  const [open, setOpen] = useState(false);
  const holdsBand = useCtaHolds().holds('band');   // 3h3: đã tick ở Sổ ⇒ nút đỏ chuyển xuống thanh dính, nút ở dải hạ thành viền
  const [moreOpen, setMoreOpen] = useState(false);   // 3k-c: «và n mục khác» — mở tại chỗ các mục chữ vượt trần 3
  const [form, setForm] = useState<StartForm>(defaultStartForm);
  const [est, setEst] = useState<NewStoryEstimate | null | undefined>(undefined);   // undefined = đang ước; null = tải lỗi
  const [estTick, setEstTick] = useState(0);   // đổi ⇒ ước lại (sau 409 ESTIMATE_CHANGED / ESTIMATE_UNKNOWN)
  const [serverError, setServerError] = useState<{ text: string; level: 'error' | 'warning' } | null>(null);
  // 3h5b lượt 2 (R4): thao tác «Chạy tiếp» chưa rõ kết quả (mất response, 5xx/502/timeout) giữ NGUYÊN {storyId, runId, expectedRevision, requestId} trong sessionStorage (try/catch; lùi về bộ nhớ) — sống qua
  // refresh dữ liệu workbench VÀ tải lại trang; chỉ xoá khi thành công hoặc lỗi 4xx nghiệp vụ chứng minh BE chưa ghi gì.
  const pendingStore = useMemo(() => sessionPendingStore(storyId), [storyId]);
  const continuing = useRef(false);   // chặn bấm đôi trong cùng tick (state `working` cập nhật muộn)
  const starting = useRef(false);   // 3i: chặn bấm đôi «Bắt đầu» (ước → gửi): cờ dùng chung giữa các lần bấm, `quickStart` đặt/trả

  // Mở khung nhập ⇒ kiểm điều kiện chạy Chặng 1 (không AI). Lỗi / chưa có số ⇒ nút xám kèm lý do; số «tối đa» không hiện nhưng vẫn là `expectedMaxUsd` gửi BE.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setEst(undefined);
    adaptationApi.newStoryRunEstimate(storyId).then((r) => { if (alive) setEst(r); }).catch(() => { if (alive) setEst(null); });
    return () => { alive = false; };
  }, [open, storyId, estTick]);

  // 261025 E3-P3f — Chặng 3 «Duyệt & đẩy N tập»: xem thử CHỈ khi chuỗi truyện mới đã XONG (không gọi lúc đang chạy / chờ người).
  const pushEps = workbench?.episodes;
  const pushItems = useMemo(() => batchItemsOf(pushEps ?? [], (pushEps ?? []).filter(isPushable).map((e) => e.ep)), [pushEps]);
  const chainDone = workbench?.appendRun?.kind === 'NEW_STORY' && workbench.appendRun.status === 'DONE';
  const batchView = workbench?.handoffBatch ?? null;
  const previewState = useHandoffPreview(storyId, pushItems, dataVersion, !!chainDone && !reviewing && batchView?.status !== 'RUNNING');
  const batchActions = useHandoffBatchActions(storyId, batchView, refreshAll);

  if (!workbench) return null;
  const push = pushStripOf(workbench.episodes, batchView, previewState);
  const recheck = recheckStripOf(workbench);   // 3j0 (P2): tập «Cần kiểm lại» (BE `qualityReady`) ⇒ nút đỏ của dải DONE = «Kiểm lại"
  const approveBlock = nsApproveBlock(data.record, workbench.chapterUnitMax);
  const bar = newStoryBar(workbench, { stage, dismissedId, approveBlock, push, batch: batchView, reviewNeed, recheck, autoRepair: autoRepairStripOf(workbench.autoRepair) });
  if (bar.kind === 'none') return null;
  const errors = startFormErrors(form, workbench.chapterUnitMax);   // 3g Q5: vượt số chương ⇒ ô đỏ + khoá «Bắt đầu» (BE vẫn kiểm lại)
  const episodesHint = startEpisodesHint(workbench.chapterUnitMax);
  const hasErrors = Object.keys(errors).length > 0;
  const progress = nsProgressText(workbench, workbench.jobs.running);
  const reviewVisible = workbench.episodes.some((e) => e.written === 'draft');
  // 3h1 (H3): «Kiểm trước khi bấm» ✓/!/✗ từ dữ liệu thật (`preflightView`); tên gốc rò lấy từ xem thử đẩy của dải khi có.
  const leakEpisodes = previewState.status === 'ok' ? previewState.data.episodes.filter((e) => (e.leaks?.length ?? 0) > 0).map((e) => e.episode) : [];
  const pre = preflightOf(bar.kind, workbench, {
    approveBlock, disabledReason: bar.kind === 'wait_skeleton' ? bar.primary?.disabledReason ?? null : null, push, leakEpisodes,
    planReasons: workbench.appendRun?.status === 'WAIT_EPISODE_PLAN' ? planStopReasonLines(workbench.appendRun.planStopReasons) : [],
  });
  const ev = estimateView(est);
  const chain = bandChainOf(workbench.chainProgress, bar);   // 3h4a: «Chấm → Sửa vòng k/2 → Chấm lại» từ BE; thiếu / null ⇒ rỗng ⇒ dải như cũ
  // 3k-c: dải GỌN nhưng ĐỦ — một câu + một nút đỏ + nút phụ hay dùng cạnh nó + tối đa 3 mục chữ hiện thẳng («và n mục khác» mở tại chỗ) + «⋯» việc hiếm. Định tuyến: xem `compactBandView.ts`; lý do CHẶN luôn hiện.
  const cb = compactBandOf(bar, pre, {
    episodes: workbench.episodes, push, reviewVisible, reviewNeed: reviewNeed ?? 0, progress, autoFix: autoFixTotals(workbench.episodes, data.scripts), chainRows: chain.rows.length,
  });
  const wait = waitBudgetView(budgetOf(workbench), waitBudgetCtxOf(workbench));   // CÙNG ngữ cảnh với dải (`newStoryBar`) ⇒ nút «Chạy tiếp» và quyết định B11 khớp nhau

  const guarded = async (fn: () => Promise<void>) => {
    if (working) return;
    setWorking(true);
    try { await fn(); } catch (e) { await handleAppendError(e, { goTo, refreshAll }); } finally { setWorking(false); }
  };

  const submitStart = async () => {
    if (working || hasErrors || ev.button.disabled || ev.button.expectedMaxUsd === null) return;   // không có số «tối đa» đã xem thì không gửi (BE bắt buộc `expectedMaxUsd`)
    setWorking(true);
    setServerError(null);
    try {
      await adaptationApi.newStoryRunStart(storyId, startBodyOf(form, ev.button.expectedMaxUsd));
      setOpen(false);
      message.success('Đang chạy chặng 1 — quét, dựng khung xương, tổng quan (chạy ở nền)');
      await refreshAll();
    } catch (e) {
      const v = startErrorView(e);
      if (v.kind === 'changed') {
        setServerError({ text: v.text, level: 'warning' });
        setEstTick((n) => n + 1);   // ước lại ⇒ số mới hiện ở nút để bấm lại; giữ khung mở và các ô đã nhập
      } else {
        setServerError({ text: v.text, level: 'error' });   // câu theo mã lỗi, không in chữ thô của BE
        if (v.kind === 'unknown') setEstTick((n) => n + 1);
        await refreshAll();
      }
    } finally { setWorking(false); }
  };

  // 3i: hộp cài đặt chuyển vào menu «⋯» — mở với đầu vào đã lưu (không có ⇒ mặc định cũ của hộp). Hành vi hộp giữ NGUYÊN (ước khi mở, «Bắt đầu» trong hộp gửi như trước).
  const openCustomStart = () => { setForm(savedStartForm(workbench)); setServerError(null); setOpen(true); };
  // 3i: nút đỏ «Bắt đầu» gửi LUÔN. `quickStart` kiểm form → ước → gửi (cùng body hộp gửi khi người không sửa gì); khoá nút trong lúc gửi (`working` + cờ `starting`) ⇒ bấm đôi không gửi hai lần.
  const startNow = async () => {
    if (starting.current || working) return;
    setWorking(true);   // giữ khoá TỚI SAU `refreshAll` (như hộp cũ): trong lúc tải lại, dải vẫn còn «Bắt đầu» ⇒ không cho bấm lần hai
    try {
      const out = await quickStart(starting, {
        form: savedStartForm(workbench), chapterUnitMax: workbench.chapterUnitMax,
        estimate: () => adaptationApi.newStoryRunEstimate(storyId), start: (b) => adaptationApi.newStoryRunStart(storyId, b),
      });
      switch (out.kind) {
        case 'started':
          message.success('Đang chạy chặng 1 — quét, dựng khung xương, tổng quan (chạy ở nền)');
          await refreshAll();
          break;
        case 'invalid':   // cài đặt đã lưu không hợp lệ (vd số tập vượt số chương) ⇒ không gửi; mở hộp để sửa
          setForm(out.form); setServerError(null); setOpen(true);
          message.info('Cài đặt đã lưu chưa hợp lệ — sửa trong hộp rồi bấm Bắt đầu', 6);
          break;
        case 'noEstimate':
          message.warning(redactMoney(out.text), 8);
          break;
        case 'failed':
          if (out.view.kind === 'changed') message.warning(redactMoney(out.view.text), 8);   // bấm lại ⇒ ước lại ở lần bấm sau
          else { message.error(redactMoney(out.view.text), 8); await refreshAll(); }
          break;
        default: break;
      }
    } finally { setWorking(false); }
  };

  // «Chạy tiếp» ở dải chờ quyết (B11): `wait.decision` đã tính sẵn mức mới (không kẹp ceiling; vượt ⇒ nút mờ, không tới đây). Gọi route nâng-và-chạy-tiếp ĐÚNG MỘT lần,
  // CAS theo `revision` của sổ đang xem (lỗi mạng ⇒ không tải lại ⇒ bấm lại gửi cùng `revision`); 409 (sổ/chuỗi đã đổi) ⇒ tải lại. Không có khung nhập số, không hiện số.
  const raiseRunId = workbench.newStoryRun?.runId ?? workbench.appendRun?.id ?? null;
  // 3h5b: BE có `continuation` ⇒ route `continue-auto-budget` (BE tự tính trần, không số từ UI, idempotent theo requestId): MỘT bấm cho cả nâng lẫn chạy lại. Không có `continuation` ⇒ cầu nối 3h5a bên dưới.
  const continueViaServer = async () => {
    if (!raiseRunId || wait.revision === null) { await refreshAll(); return; }
    if (continuing.current) return;
    continuing.current = true;
    try {
      const r = await submitContinue({ storyId, runId: raiseRunId, revision: wait.revision }, {
        store: pendingStore,
        post: (b) => adaptationApi.newStoryRunContinueAutoBudget({ storyId: b.storyId, runId: b.runId, expectedRevision: b.expectedRevision, requestId: b.requestId }),
      });
      if (r.ok) {
        const o = autoContinueOutcomeView(r.result);
        if (o.level === 'success') message.success(o.text, 6); else message.warning(o.text, 10);
        await refreshAll();
      } else {
        const v = autoContinueErrorView(r.error);
        message.warning(v.text, 8);
        if (v.reload) await refreshAll();
      }
    } finally { continuing.current = false; }
  };
  const continueBudget = () => guarded(async () => {
    if (wait.viaServer) { await continueViaServer(); return; }
    const d = wait.decision;
    if (d.kind !== 'raise' || !raiseRunId) { await refreshAll(); return; }
    try {
      const r = await adaptationApi.newStoryRunRaiseCap({ storyId, runId: raiseRunId, expectedRevision: d.expectedRevision, newCapUsd: d.newCapUsd });
      const o = continueOutcomeView(r);
      if (o.level === 'success') message.success(o.text, 6); else message.warning(o.text, 10);
      await refreshAll();
    } catch (e) {
      const v = continueErrorView(e);
      message.warning(v.text, 8);
      if (v.reload) await refreshAll();
    }
  });

  const resumeBeforeSave = () => guarded(async () => {
    const runId = workbench.newStoryRun?.runId;
    if (!runId) return;
    const r = await adaptationApi.newStoryRunStart(storyId, { resumeRunId: runId });
    // 3d3: quyển chưa sạch giữ nguyên (chưa ai sửa) ⇒ BE không mở job (`jobId null`, `waiting 'REVIEW_GATE'`) — nói thẳng, KHÔNG báo «Đang chạy».
    if (!r.jobId && r.waiting === 'REVIEW_GATE') message.info(VOLUME_GATE_NOTHING_TO_RERUN, 8);
    else message.success('Đang chạy tiếp chặng 1');
    await refreshAll();
  });
  // 3d3: «Chạy tiếp, để lỗi xem sau» ở cổng ① quét quyển — hộp xác nhận một bước, rồi cùng route với «Chạy tiếp» + cờ `acceptVolumeIssues` (BE ghi dấu «để lỗi xem sau» và mở khung xương trong MỘT giao dịch; 0 AI).
  const acceptVolumeIssues = () => guarded(async () => {
    const runId = workbench.newStoryRun?.runId;
    if (!runId) { await refreshAll(); return; }
    const c = volumeGateWaitView(workbench.newStoryRun?.volumeGate).confirm;
    if (!(await askConfirm({ title: c.title, content: c.content, okText: c.okText }))) return;
    await adaptationApi.newStoryRunStart(storyId, { resumeRunId: runId, acceptVolumeIssues: true });
    message.success('Đã chạy tiếp — máy dựng khung xương ở nền; việc để lỗi được ghi lại');
    await refreshAll();
  });
  const resumeAfterSave = () => guarded(async () => { await adaptationApi.appendRunContinue(storyId); await refreshAll(); });
  // Chỗ dừng 1: nút chính ở dải. Mốc `modifiedAt` lấy từ bản ② đang tải ở trang (`data.record`, cùng nguồn nút của ②) — không gọi route nào thêm.
  // E3-P3c2b: gửi thêm `expectedMaxUsd` = đúng SỐ THÔ «tối đa Chặng 2» đang hiện trên nhãn nút (`a.expectedMaxUsd`, từ `newStoryBar`); BE tính lại, lệch ⇒ 409 `ESTIMATE_CHANGED` (không tự gửi lại).
  const approveSkeleton = (a: NsAction) => guarded(async () => {
    const modifiedAt = data.record?.modifiedAt;
    if (!modifiedAt) { message.warning('Chưa có mốc của bản đang xem — đã tải lại, thử lại', 5); await refreshAll(); return; }
    if (typeof a.expectedMaxUsd !== 'number') { message.warning('Chưa có thông tin chạy Chặng 2 — đã tải lại, thử lại', 5); await refreshAll(); return; }
    try {
      await adaptationApi.appendRunContinue(storyId, modifiedAt, a.expectedMaxUsd, 'SERVER_ESTIMATE');
      message.success('Đã duyệt — máy chạy tiếp chặng 2 ở nền (bản địa hoá, chia tập, viết)');
      await refreshAll();
    } catch (e: any) {
      const s1 = stop1ErrorView(e);
      if (s1.kind !== 'other') { message.warning(s1.text, 8); if (s1.reload) await refreshAll(); return; }   // số ước đổi / chưa ước được / chuỗi đã đổi ⇒ báo ngắn rồi tải lại
      const code = e?.response?.data?.code;
      if (typeof code === 'string' && (code.startsWith('OVERVIEW_') || code === 'OUTLINE_CHANGED' || code === 'EXPECTED_MODIFIED_AT_REQUIRED' || code === 'INVALID_EXPECTED_MODIFIED_AT')) {
        const v = overviewErrorView(e, 'Không chạy tiếp được');
        if (v.exit === 'reload' || v.exit === 'wait' || v.exit === 'none') message.warning(v.text, 8); else message.error(v.text, 8);
        if (v.exit !== 'reload' && v.exit !== 'wait' && v.exit !== 'none') goTo({ stage: 'skeleton', intent: 'openOverview' });   // lối ra (tạo lại / gộp lại / sửa) làm ở Tổng quan truyện
        if (errorShouldReload(v)) await refreshAll();
      } else await handleAppendError(e, { goTo, refreshAll });   // HAS_ERRORS ⇒ mở ② + hộp báo
    }
  });
  // 3d1: «Tôi đã sửa — chấm lại». Cùng đường «Chạy tiếp»: BE thấy nội dung đã đổi ⇒ mở job chỉ-cổng chấm lại từ vòng 0; chưa ai sửa ⇒ giữ nguyên trạng thái chờ, 0 lời gọi AI.
  const regradeGate = () => guarded(async () => {
    await adaptationApi.appendRunContinue(storyId);
    message.info('Đã gửi — nếu bạn có sửa Bản địa hoá thì máy chấm lại; chưa sửa gì thì giữ nguyên, vẫn chờ bạn', 6);
    await refreshAll();
  });
  // 3d1 (D8): «Chạy tiếp, để lỗi xem sau» — hộp xác nhận MỘT bước, rồi route D8 (audit + sang chia tập trong một CAS, 0 AI). Mốc = bản ② đang tải (cùng nguồn nút duyệt).
  const continueReviewGate = () => guarded(async () => {
    const gw = workbench.appendRun?.gateWait; const runId = workbench.appendRun?.id; const modifiedAt = data.record?.modifiedAt;
    if (!gw || !runId) { await refreshAll(); return; }
    if (!modifiedAt) { message.warning('Chưa có mốc của bản đang xem — đã tải lại, thử lại', 5); await refreshAll(); return; }
    const c = gateWaitView(gw).confirm;
    if (!(await askConfirm({ title: c.title, content: c.content, okText: c.okText }))) return;
    await adaptationApi.newStoryRunReviewGateContinue({ storyId, runId, step: gw.step, expectedModifiedAt: modifiedAt });
    message.success('Đã chạy tiếp — máy chia tập ở nền; lỗi cổng được ghi lại để xem sau');
    await refreshAll();
  });
  const approvePlan = () => guarded(async () => {
    const modifiedAt = data.record?.modifiedAt;
    if (!modifiedAt) { message.warning('Dàn tập vừa đổi — đã tải lại, thử lại', 5); await refreshAll(); return; }
    try {
      await submitEpisodePlanApproval(adaptationApi, { storyId, modifiedAt, appendWaiting: true });
      message.success('Đã chốt dàn tập và bắt đầu viết tập');
      await refreshAll();
    } catch (e) {
      const v = episodePlanSaveErrorView(e, 'Không chốt được dàn tập');
      if (v.level === 'warning') message.warning(v.text, 6); else message.error(v.text, 6);
      if (v.exit === 'openOverview') goTo({ stage: 'skeleton', intent: 'openOverview' });
      await refreshAll();
    }
  });
  const cancel = () => guarded(async () => {
    const c = nsCancelCopy();
    if (!(await askConfirm({ title: c.title, content: c.content, okText: c.okText, danger: c.danger }))) return;
    await adaptationApi.appendRunCancel(storyId);
    message.success('Đã dừng chuỗi truyện mới');
    await refreshAll();
  });

  // Đẩy đúng danh sách tập đã xem thử (`push.episodes`); băm lấy từ workbench đang xem — máy chủ so lại, lệch ⇒ 409 `SCRIPT_CHANGED` (hook báo + tải lại, không tự gửi lại).
  const pushBatch = async () => {
    if (working || batchActions.starting || push.disabled || push.episodes.length === 0) return;
    await batchActions.start(workbench.episodes, push.episodes);
  };

  // 3j0 (P2): «Kiểm lại» = ĐÚNG route chấm lại C2 đang có (một tập, job nền, `modifiedAt` bản đang xem làm CAS); lỗi 409 hiện chữ của máy chủ. Sổ tiền cho chấm lại: TODO 3j2.
  const recheckNow = () => guarded(async () => {
    const target = recheck?.target;
    const stamp = workbench.episodes.find((e) => e.ep === target)?.modifiedAt;
    if (!target || !stamp) { message.warning('Chưa có mốc thời gian của bản kịch bản — tải lại rồi thử lại'); return; }
    try { await adaptationApi.rejudgeC2(storyId, target, stamp); } catch (e) { message.error(rejudgeFailureToast(e)); }
    await refreshAll();
  });

  const run = (a: NsAction) => {
    if (a.target) { goTo(a.target); return; }
    switch (a.id) {
      case 'start': void startNow(); break;
      case 'continueBudget': void continueBudget(); break;
      case 'resumeBeforeSave': void resumeBeforeSave(); break;
      case 'acceptVolumeIssues': void acceptVolumeIssues(); break;
      case 'resumeAfterSave': void resumeAfterSave(); break;
      case 'approveSkeleton': void approveSkeleton(a); break;
      case 'approvePlan': void approvePlan(); break;
      case 'regradeGate': void regradeGate(); break;
      case 'continueReviewGate': void continueReviewGate(); break;
      case 'cancel': void cancel(); break;
      case 'pushBatch': void pushBatch(); break;
      case 'cancelBatch': if (batchView) void batchActions.cancel(batchView.jobId); break;
      case 'openBatchResults': batchActions.openResults(); break;
      case 'openReview': openReview(); break;
      case 'recheck': void recheckNow(); break;
      case 'autoRepairStart': void autoRepair.start(); break;
      case 'autoRepairStop': void autoRepair.stop(); break;
      default: break;
    }
  };
  // `busy` (job con / chuỗi tự chạy / việc ở ②) khoá cả nút duyệt ở dải, như nút cùng việc ở ②/④; khi chờ người `busy` = false nên không kẹt. Dùng chung cho nút chính và mục «⋯».
  const actionDisabled = (a: NsAction): boolean => !!a.disabled || working || (a.id === 'pushBatch' && (batchActions.starting || busy)) || (a.id === 'autoRepairStart' && (autoRepair.starting || busy)) || (a.id === 'autoRepairStop' && autoRepair.stopping) || ((a.id === 'start' || a.id === 'approveSkeleton' || a.id === 'approvePlan' || a.id === 'continueBudget' || a.id === 'regradeGate' || a.id === 'continueReviewGate' || a.id === 'acceptVolumeIssues') && busy);
  const btn = (a: NsAction, primary: boolean) => {
    // 3h1 (K3 / H3): đúng MỘT nút đỏ (primary) ở dải — `WbButton` hạ mọi nút không phải CTA về trung tính. 3k-c: nút phụ hay dùng (Mở sổ duyệt, Dừng chuỗi…) quay lại cạnh nút đỏ, vẫn trung tính.
    const el = <Button key={a.id} size="middle" className="wb-band-cta" type="primary" cta={primary && holdsBand} disabled={actionDisabled(a)} onClick={() => run(a)}>{a.label}</Button>;
    return a.disabledReason ? <Tooltip key={a.id} title={a.disabledReason}><span>{el}</span></Tooltip> : el;
  };
  const sideBtns = cb.buttons.filter((m) => m.action).map((m) => {
    const a = m.action!;
    const el = <Button key={m.key} size="middle" className="wb-band-sec" disabled={actionDisabled(a)} onClick={() => run(a)}>{m.label}</Button>;
    return m.reason ? <Tooltip key={m.key} title={m.reason}><span>{el}</span></Tooltip> : el;
  });
  const menuItems = cb.menu.map((m) => ({
    key: m.key, label: m.reason ? <span title={m.reason}>{m.label}</span> : m.label,
    disabled: m.key === 'customStart' ? working || busy : m.action ? actionDisabled(m.action) : !!m.disabled,
  }));
  const onMenu = (key: string) => {
    const it = cb.menu.find((m) => m.key === key);
    if (!it) return;
    if (key === 'customStart') openCustomStart();
    else if (key === 'dismiss') { if (bar.dismissId) onDismiss(bar.dismissId); }
    else if (it.action) run(it.action);
  };
  // 3k-c: các mục chữ hiện thẳng dưới câu (≤ 3, mục chặn trước); mục «Kiểm trước…» liền nhau gom dưới một tiêu đề.
  const { shown, hidden } = bandItemsOf(cb);
  const renderItems = (items: BandItem[], keyBase: string) => {
    const out: React.ReactNode[] = [];
    let headed = false;   // tiêu đề «Kiểm trước…» chỉ một lần dù các mục ✓ / ! bị xen dòng khác (cảnh báo xếp trước)
    for (let i = 0; i < items.length;) {
      const it = items[i];
      if (it.kind === 'check') {
        const group: BandItem[] = [];
        while (i < items.length && items[i].kind === 'check') { group.push(items[i]); i += 1; }
        const showHead = !headed; headed = true;
        out.push(
          <div className="wb-ck" key={`${keyBase}ck${i}`} aria-label={cb.detailTitle}>
            {showHead && <b className="wb-ck-h">{cb.detailTitle}:</b>}
            {group.map((g, j) => {
              const c = (g as Extract<BandItem, { kind: 'check' }>).item;
              return (
                <span key={j} className={`wb-ck-i wb-ck-${c.s}`} data-testid="band-item">
                  <i aria-hidden="true">{c.s === 'ok' ? '✓' : '!'}</i>
                  <span>{c.t}{c.link && <> <Button type="link" size="small" className="wb-ck-lk" onClick={() => (c.link!.action === 'openReview' ? openReview() : c.link!.target && goTo(c.link!.target))}>{c.link.label}</Button></>}</span>
                </span>
              );
            })}
          </div>,
        );
      } else {
        const k = `${keyBase}${i}`;
        i += 1;
        if (it.kind === 'blocking') out.push(<div key={k} className="wb-band-line wb-band-warn" role="alert" data-testid="band-item">{it.text}</div>);   // Lý do CHẶN luôn hiện
        else if (it.kind === 'line') {
          out.push(it.skipped && reviewVisible
            ? <div key={k} className="wb-band-line" data-testid="band-item"><Button type="link" size="small" className="wb-band-skip" onClick={openReview}>{it.text}</Button></div>
            : <div key={k} className={`wb-band-line${it.warn ? ' wb-band-warn' : ''}`} data-testid="band-item">{it.text}</div>);
        } else if (it.kind === 'fix') {
          out.push(
            <div key={k} className="wb-band-line" data-testid="band-item">
              {it.text}
              {it.episodes.map((n) => <Button key={n} type="link" size="small" className="wb-ck-lk" onClick={() => goTo({ stage: 'script', episode: n, intent: 'openEpisode' })}>Xem tập {n}</Button>)}
            </div>,
          );
        } else if (it.kind === 'calls') out.push(<div key={k} className="wb-band-line" data-testid="band-item">{it.text}</div>);
      }
    }
    return out;
  };
  const BAND_GLYPH: Partial<Record<typeof cb.kind, string>> = { start: '▶', start_blocked: '▶', done: '✓', cancelled: '■' };
  const glyph = cb.kind === 'running' || cb.buttons.some((b) => b.key === 'autoRepairStop') ? null : (cb.tone === 'done' ? '✓' : BAND_GLYPH[cb.kind] ?? '!');

  const set = <K extends keyof StartForm>(k: K, v: StartForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="wb-band-wrap">
      <BandCtaReporter id="newstory" active={!!bar.primary && !bar.primary.disabled} />
      <section className={`wb-band wb-band-compact wb-band-${cb.tone}`} role="status" aria-label="Máy tự chạy ba chặng" data-tour="band">
        <div className="wb-band-top">
          <span className={`wb-band-ic${glyph === null ? ' wb-band-ic-run' : ''}`} aria-hidden="true" title={BAND_TIP}>{glyph === null ? <span className="wb-band-spin" /> : glyph}</span>
          <div className="wb-band-msg"><b className="wb-band-sentence">{cb.sentence}</b></div>
          <div className="wb-band-act">
            {sideBtns}
            {bar.primary && btn(bar.primary, true)}
            {menuItems.length > 0 && (
              <Dropdown trigger={['click']} menu={{ items: menuItems, onClick: ({ key }) => onMenu(String(key)) }}>
                <Button size="small" type="text" aria-label="Thêm tác vụ của dải" title="Thêm tác vụ của dải">⋯</Button>
              </Dropdown>
            )}
          </div>
        </div>
        {renderItems(moreOpen ? [...shown, ...hidden] : shown, 's')}   {/* mở ra ⇒ MỘT lượt vẽ (mục «Kiểm trước…» liền nhau không bị tách đôi hai tiêu đề) */}
        {hidden.length > 0 && (
          <>
            <button type="button" className="wb-band-more" aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}>{moreOpen ? 'Thu gọn' : `và ${hidden.length} mục khác`}</button>
          </>
        )}
        {chain.rows.length > 0 && <ChainLoop rows={chain.rows} caption={chain.caption} variant="band" />}
      </section>

      <Modal
        title="Bắt đầu chuyển thể truyện mới" open={open} onCancel={() => { if (!working) setOpen(false); }}
        okText={ev.button.label} cancelText="Thôi" onOk={submitStart} confirmLoading={working} okButtonProps={{ disabled: hasErrors || working || ev.button.disabled }} maskClosable={false} destroyOnClose
      >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {serverError && <Alert type={serverError.level} showIcon message={serverError.text} />}
          <div>
            <Text>Thể loại</Text>
            <Input value={form.genre} placeholder="chưa rõ" onChange={(e) => set('genre', e.target.value)} aria-label="Thể loại" />
          </div>
          <Space wrap align="start">
            <div>
              <Text>Số tập (để trống: AI đề xuất)</Text><br />
              <Input value={form.episodes} style={{ width: 150 }} inputMode="numeric" onChange={(e) => set('episodes', e.target.value)} status={errors.episodes ? 'error' : undefined} aria-label="Số tập" />
              {errors.episodes ? <div><Text type="danger" style={{ fontSize: 13 }}>{errors.episodes}</Text></div> : episodesHint && <div><Text type="secondary" style={{ fontSize: 13 }}>{episodesHint}</Text></div>}
            </div>
            <div>
              <Text>Phút mỗi tập</Text><br />
              <InputNumber value={Number(form.minutesPerEpisode) || undefined} min={0.5} step={0.5} style={{ width: 120 }} onChange={(v) => set('minutesPerEpisode', v === null || v === undefined ? '' : String(v))} status={errors.minutesPerEpisode ? 'error' : undefined} aria-label="Phút mỗi tập" />
              {errors.minutesPerEpisode && <div><Text type="danger" style={{ fontSize: 13 }}>{errors.minutesPerEpisode}</Text></div>}
            </div>
          </Space>
          <Space wrap align="start">
            <div>
              <Text>Mức chuyển thể</Text><br />
              <Select<StartForm['adaptMode']> value={form.adaptMode} style={{ width: 170 }} onChange={(v) => set('adaptMode', v)} options={Object.entries(ADAPT_MODE_LABEL).map(([value, label]) => ({ value: value as StartForm['adaptMode'], label }))} aria-label="Mức chuyển thể" />
            </div>
            <div>
              <Text>Ngôn ngữ kịch bản</Text><br />
              <Input value={form.language} style={{ width: 150 }} onChange={(e) => set('language', e.target.value)} aria-label="Ngôn ngữ" />
            </div>
          </Space>
          <div>
            <Text>Bản địa hoá</Text>
            <Space wrap align="start" style={{ display: 'flex' }}>
              <Select<StartForm['policyMode']> value={form.policyMode} style={{ width: 280 }} onChange={(v) => set('policyMode', v)} options={Object.entries(POLICY_MODE_LABEL).map(([value, label]) => ({ value: value as StartForm['policyMode'], label }))} aria-label="Chính sách bản địa hoá" />
              {form.policyMode === 'MAP' && (
                <div>
                  <Input value={form.targetCulture} placeholder="vd Persian" style={{ width: 180 }} onChange={(e) => set('targetCulture', e.target.value)} status={errors.targetCulture ? 'error' : undefined} aria-label="Nền văn hoá đích" />
                  {errors.targetCulture && <div><Text type="danger" style={{ fontSize: 13 }}>{errors.targetCulture}</Text></div>}
                </div>
              )}
            </Space>
            <Text type="secondary" style={{ fontSize: 13 }}>Truyện này đang bật bản địa hoá (chỉ đọc ở đây).</Text>
          </div>
          <div>
            {ev.button.disabled && ev.button.reason && <div><Text type={ev.state === 'loading' ? 'secondary' : 'danger'} style={{ fontSize: 13 }}>{ev.button.reason}</Text></div>}
          </div>
        </Space>
      </Modal>

      <HandoffBatchResults batch={batchView} open={batchActions.resultsOpen} onClose={batchActions.closeResults} onCancel={(id) => { void batchActions.cancel(id); }} />
    </div>
  );
};

export default NewStoryRunBar;
